import { checkoutPlatform } from '../lib/hostedCheckout'
import { useMemberBookingRecovery } from '../lib/useMemberBookingRecovery'
import { formatFacilityBookingDate } from '../utils/facilityBookingPresentation'
import StableFacilityImage from '../components/StableFacilityImage'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import MemberFacilityCheckout from '../components/MemberFacilityCheckout'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Clock, Shield, Users } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import BackPillButton from '../components/BackPillButton'
import MemberPaymentMethodOptions from '../components/MemberPaymentMethodOptions'
import { createPayNowHandoff, openPayNowCheckoutPlaceholder, subscribePayNowCompleted, updatePayNowHandoff } from '../utils/payNowTabHandoff'
import { commitMemberBookingSuccess, createCommittedBooking } from '../lib/upcomingBookingsCache'
import { getFitnessMemberErrorMessage } from '../utils/fitnessMemberErrors'
import {
  DEFAULT_GOLF_TIME_ZONE,
  normalizeDateKey,
} from '../utils/golfTime'

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
}

function formatPrice(value, currency, locale) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  const code = currency || ''
  const symbol = CURRENCY_SYMBOLS[code] || ''
  const formatted = numeric.toLocaleString(locale, { minimumFractionDigits: numeric % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })
  if (symbol) return `${symbol}${formatted}`
  if (code) return `${formatted} ${code}`
  return formatted
}

function normalizeSlot(value) {
  if (!value) return null
  return String(value).trim()
}

export default function MemberFitnessBookingPage({ clubCode, member }) {
  const { t, locale } = useOptionalPrivanaI18n()
  const { classId } = useParams()
  const memberName = member?.full_name || [member?.first_name || member?.firstName, member?.last_name || member?.lastName].filter(Boolean).join(' ') || t('facilityBooking.member')
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()



  const occurrenceId = searchParams.get('occurrence_id')
  const selectedDateKey = useMemo(() => normalizeDateKey(searchParams.get('date')), [searchParams])
  const slotStartAt = useMemo(() => normalizeSlot(searchParams.get('start_at')), [searchParams])
  const slotStartTime = useMemo(() => normalizeSlot(searchParams.get('start_time')), [searchParams])

  const [fitnessClass, setFitnessClass] = useState(location.state?.fitnessClass || null)
  const [fitnessClassLoading, setFitnessClassLoading] = useState(!location.state?.fitnessClass)
  const [fitnessClassError, setFitnessClassError] = useState(null)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)
  const [price, setPrice] = useState(null)
  const [currency, setCurrency] = useState('GBP')
  const [selectedSlot, setSelectedSlot] = useState(location.state?.slot || null)
  const [saving, setSaving] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('member_account')
  const [submitError, setSubmitError] = useState(null)
  const recovery = useMemberBookingRecovery({ clubCode, module: 'fitness', memberId: member?.id || member?.member_id, post: memberPortalApiPost,
    onCompleted: bookingId => navigate(`/${clubCode}/fitness/booking/success?booking_id=${bookingId}`, { replace: true }),
    onReleased: () => { submissionLock.current = false; setSubmitError(null) },
  })
  const outcomePending = recovery.pending
  useEffect(() => subscribePayNowCompleted({ module: 'fitness', clubCode, onCompleted: () => recovery.reconcile() }), [clubCode, recovery.reconcile])
  const flowActive = useRef(true)
  useEffect(() => {
    flowActive.current = true
    return () => { flowActive.current = false }
  }, [])
  const submissionLock = useRef(false)
  const errorRef = useRef(null)
  useEffect(() => {
    if (submitError) { errorRef.current?.scrollIntoView?.({ block: 'center', behavior: 'instant' }); errorRef.current?.focus({ preventScroll: true }) }
  }, [submitError])

  useEffect(() => {
    if (selectedDateKey && (slotStartAt || slotStartTime)) return
    navigate(`/${clubCode}/fitness/${classId}${selectedDateKey ? `?date=${selectedDateKey}` : ''}`, { replace: true })
  }, [classId, clubCode, navigate, selectedDateKey, slotStartAt, slotStartTime])

  useEffect(() => {
    let cancelled = false

    async function loadFitnessClass() {
      if (!clubCode || !classId || fitnessClass) return
      setFitnessClassLoading(true)
      setFitnessClassError(null)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/fitness/classes/${classId}`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_class')
        if (!cancelled) setFitnessClass(json?.data || null)
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load fitness class', err)
          setFitnessClassError(getFitnessMemberErrorMessage(err?.message, key => key))
        }
      } finally {
        if (!cancelled) setFitnessClassLoading(false)
      }
    }

    loadFitnessClass()
    return () => {
      cancelled = true
    }
  }, [classId, clubCode, fitnessClass, memberPortalApiGet])

  useEffect(() => {
    let cancelled = false

    async function loadAvailability() {
      if (!clubCode || !classId || !selectedDateKey) return
      setAvailabilityLoading(true)
      setAvailabilityError(null)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/fitness/classes/${classId}/availability?date=${selectedDateKey}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'availability_unavailable')
        if (cancelled) return
        const slots = Array.isArray(json?.slots) ? json.slots : []
        const matchedSlot = slots.find((slot) => {
          if (occurrenceId && slot?.occurrence_id !== occurrenceId) return false
          if (slotStartAt && slot?.start_at) return normalizeSlot(slot.start_at) === slotStartAt
          if (slotStartTime && slot?.start_time) return normalizeSlot(slot.start_time) === slotStartTime
          return false
        })

        setSelectedSlot(matchedSlot || null)
        setTimeZone(json?.time_zone || DEFAULT_GOLF_TIME_ZONE)
        setPrice(matchedSlot?.resolved_price_total ?? matchedSlot?.price ?? json?.price ?? fitnessClass?.price ?? null)
        setCurrency(
          matchedSlot?.resolved_price_currency || matchedSlot?.currency || json?.currency || fitnessClass?.currency || 'GBP'
        )
        if (!matchedSlot) {
          setAvailabilityError('facilityBooking.unavailable')
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load fitness availability', err)
          setAvailabilityError(getFitnessMemberErrorMessage(err?.message, key => key))
          setSelectedSlot(null)
        }
      } finally {
        if (!cancelled) setAvailabilityLoading(false)
      }
    }

    loadAvailability()
    return () => {
      cancelled = true
    }
  }, [occurrenceId, classId, clubCode, fitnessClass?.currency, fitnessClass?.price, memberPortalApiGet, selectedDateKey, slotStartAt, slotStartTime])

  const className = fitnessClass?.name || t('facilityBooking.fitnessClassLabel')
  const classImage = fitnessClass?.photo_url || null
  const dateLabel = selectedDateKey ? formatFacilityBookingDate(selectedDateKey, locale) : null
  const timeLabel = selectedSlot?.start_time || slotStartTime || t('facilityBooking.selectedTime')
  const totalLabel = formatPrice(price ?? fitnessClass?.price, currency, locale) || t('facilityBooking.tbd')

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submissionLock.current || outcomePending) return
    if (!selectedDateKey || !selectedSlot?.start_time || !selectedSlot?.start_at) {
      setSubmitError('facilityBooking.invalid')
      return
    }
    submissionLock.current = true
    setSaving(true)
    setSubmitError(null)
    let requestSent = false
    let responseReceived = false
    let checkoutOpened = false
    let outcomeUncertain = false
    let checkoutWindow = null
    try {
      const bookingPayload = {
        class_id: classId,
        occurrence_id: selectedSlot.occurrence_id,
        date: selectedDateKey,
        start_time: selectedSlot.start_time,
        start_at: selectedSlot.start_at,
        request_key: recovery.requestKey(),
        payment_method: paymentMethod === 'pay_now' && Number(price) > 0 ? 'pay_now' : 'account',
        estimated_amount: Number(price ?? fitnessClass?.price ?? 0),
      }
      if (paymentMethod === 'pay_now' && Number(price) > 0) {
        const handoff = createPayNowHandoff({ module: 'fitness', clubCode, successPath: `/${clubCode}/fitness/booking/success` })
        checkoutWindow = openPayNowCheckoutPlaceholder(handoff, { locale })
        recovery.markPending()
        requestSent = true
        const checkoutRes = await memberPortalApiPost(`/api/members-portal/${clubCode}/booking-payments/checkout`, { platform: checkoutPlatform(), module: 'fitness', booking_payload: bookingPayload })
        responseReceived = true
        const checkoutJson = await checkoutRes.json().catch(() => null)
        if (!flowActive.current) { checkoutWindow?.close?.(); return }
        if(checkoutRes.ok && checkoutJson?.booking_id && checkoutJson?.status==='completed'){checkoutWindow.close();recovery.release();navigate(`/${clubCode}/fitness/booking/success?booking_id=${checkoutJson.booking_id}`);return}
        if (checkoutRes.status >= 500) throw new Error('booking_outcome_unknown')
        if (checkoutRes.ok && !checkoutJson?.url) throw new Error('booking_outcome_unknown')
        if (!checkoutRes.ok || !checkoutJson?.url) throw new Error(checkoutJson?.error || 'checkout_failed')
        updatePayNowHandoff(handoff.key, { checkout_session_id: checkoutJson.checkout_session_id || checkoutJson.session_id || checkoutJson.sessionId || null, payment_attempt_id: checkoutJson.payment_attempt_id || checkoutJson.paymentAttemptId || null })
        recovery.watchCheckout(checkoutWindow)
        await checkoutWindow.location.assign(checkoutJson.url)
        checkoutOpened = true
        recovery.markPending()
        return
      }
      recovery.markPending()
      requestSent = true
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/fitness/bookings`, bookingPayload)
      responseReceived = true
      const json = await res.json().catch(() => null)
      if (!flowActive.current) {
        window.dispatchEvent(new CustomEvent('members:upcoming-bookings-invalidated'))
        return
      }
      if (res.status >= 500) throw new Error('booking_outcome_unknown')
      if (!res.ok) {
        const errorCode = typeof json?.error === 'string' ? json.error : null
        throw new Error(errorCode || 'booking_failed')
      }
      const bookingId = json?.booking_id
      if (!bookingId) throw new Error('booking_outcome_unknown')
      recovery.release()
      const booking = createCommittedBooking({ id: bookingId, module: 'fitness', title: className, location: fitnessClass?.location_name,
        startAt: selectedSlot.start_at, timeZone, imageUrl: classImage, amount: Number(json?.price_total ?? price ?? fitnessClass?.price), currency: json?.currency || currency,
        details: { participants: [{ name: memberName }], payment_method: 'member_account', payment_summary: 'Charged to member account' } })
      commitMemberBookingSuccess(booking, { clubCode })
      navigate(`/${clubCode}/fitness/booking/success${bookingId ? `?booking_id=${bookingId}` : ''}`, { state: { booking } })
    } catch (err) {
      checkoutWindow?.close?.()
      if (!flowActive.current) return
      if ((requestSent && !responseReceived) || err?.message === 'booking_outcome_unknown') {
        outcomeUncertain = true
        recovery.markPending()
        setSubmitError('facilityBooking.unknown')
      } else { recovery.release(); setSubmitError(getFitnessMemberErrorMessage(err?.message, key => key)) }
    } finally {
      if (!checkoutOpened && !outcomeUncertain) submissionLock.current = false
      if (flowActive.current) setSaving(false)
    }
  }

  return (
    <div className="mp-tee-booking mp-tee-booking--facility">
      <div className="mp-tee-booking__header">
        <BackPillButton
          className="mp-tee-booking__back"
          to={`/${clubCode}/fitness`}
          state={fitnessClass ? { fitnessClass } : undefined}
        >
          {t('facilityBooking.backClasses')}
        </BackPillButton>
        <div className="mp-tee-booking__title">
          <h3>
            <strong>{className}</strong>{dateLabel ? ` – ${dateLabel}` : ''}{timeLabel ? ` – ${timeLabel}` : ''}
          </h3>
        </div>
        <div aria-hidden className="mp-tee-booking__spacer" />
      </div>

      {(fitnessClassError || availabilityError || submitError) && (
        <div className="mp-warning" role="alert" data-mp-static-focus="" tabIndex={-1} ref={errorRef}>{t(submitError || availabilityError || fitnessClassError)}</div>
      )}

      <form className="mp-tee-booking__grid" onSubmit={handleSubmit}>
        <div className="mp-tee-booking__media">
          <div className="mp-tee-booking__image mp-facility-review-image"><StableFacilityImage key={`${clubCode}:${classId}`} identity={`${clubCode}:${classId}`} url={classImage} /></div>
          <div className="mp-tee-booking__compact-summary"><strong>{className}</strong><span>{dateLabel} · {timeLabel}</span>{fitnessClass?.location ? <span>{fitnessClass.location}</span> : null}</div>
        </div>

        <aside className="mp-tee-booking__summary">
          <div className="mp-summary-card">
            <div className="mp-summary-header">
              <p className="mp-eyebrow">{t('facilityBooking.review')}</p>
              <h4>{t('facilityBooking.fitnessBooking')}</h4>
            </div>
            <div className="mp-summary-section">
              <div className="mp-summary-row">
                <Users size={16} />
                <div>
                  <p className="mp-label">{t('facilityBooking.attendee')}</p>
                  <p className="mp-value">{memberName}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Shield size={16} />
                <div>
                  <p className="mp-label">{t('facilityBooking.payer')}</p>
                  <p className="mp-value">{memberName}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Clock size={16} />
                <div>
                  <p className="mp-label">{t('facilityBooking.session')}</p>
                  <p className="mp-value">{timeLabel}</p>
                </div>
              </div>
            </div>
            {Number(price) > 0 ? <MemberPaymentMethodOptions value={paymentMethod} onChange={setPaymentMethod} disabled={saving} mobileCards /> : price != null ? <p>{t('facilityBooking.noPayment')}</p> : null}

          </div>
        </aside>
        <MemberFacilityCheckout recovery={saving ? null : recovery} total={totalLabel} disabled={outcomePending || fitnessClassLoading || availabilityLoading || price == null || !selectedSlot || Boolean(availabilityError)} saving={saving} label={t('facilityBooking.confirm')} savingLabel={t('facilityBooking.confirming')} reason={outcomePending ? null : fitnessClassLoading || availabilityLoading ? t('facilityBooking.checking') : !selectedSlot ? t('facilityBooking.chooseTime') : null} />
      </form>
    </div>
  )
}
